import { DOCUMENT } from "@angular/common";
import { AfterViewInit, Component, ComponentRef, forwardRef, Inject, Input, ViewContainerRef } from "@angular/core";
import { DateRange } from "@angular/material/datepicker";
import { ProductionLine, Thing } from "../../model";
import { FlatTreeNode, FlatTreeService } from "../../service/flat-tree.service";
import { TreeNodeService } from "../../service/tree-node.service";
import { TreeNode } from "../../shared/component/tree-list/tree-list.component";
import { LayoutType } from "../shared/details-widget";
import { LocationSchemaPlanViewerGridElementComponent } from "./location-schema-plan-viewer-grid-element.component";
import { LocationSchemaPlanViewData } from "./location-schema-plan-viewer.component";
import { LocationSchemaService } from "./location-schema-widget.service";

@Component({
    selector: 'location-schema-plan-viewer-details',
    template: require('./location-schema-plan-viewer-details.component.html'),
    styles: [require('./location-schema-plan-viewer-details.component.css')]
})
export class LocationSchemaPlanViewerDetailsComponent implements AfterViewInit {

    @Input() productionLine: ProductionLine;

    @Input() linkedThingIds: string[];

    @Input() planViewData: LocationSchemaPlanViewData;

    @Input() range: DateRange<moment.Moment>;

    @Input() detailsLayout: LayoutType;

    error: string;
    workCellInnerHTML: { [id: string]: any } = {};
    productionLineDisplayedValue: LocationSchemaPlanViewDisplayedValue;
    workCellDisplayedValueMap: { [id: string]: LocationSchemaPlanViewDisplayedValue };

    constructor(
        @Inject(forwardRef(() => TreeNodeService)) private treeNodeService: TreeNodeService,
        @Inject(forwardRef(() => FlatTreeService)) private flatTreeService: FlatTreeService,
        @Inject(DOCUMENT) private document: Document,
        @Inject(forwardRef(() => ViewContainerRef)) private vcRef: ViewContainerRef,
        @Inject(forwardRef(() => LocationSchemaService)) private locationSchemaService: LocationSchemaService,
    ) { }

    ngAfterViewInit(): void {
        const productionLineThingIds = this.locationSchemaService.getThingIdsFromProductionLine(this.productionLine);
        const productionLineThings = productionLineThingIds.map(id => this.planViewData.thingDataMap[id].thing);
        this.productionLine.workCells.forEach(wc => {
            const wcThings = productionLineThings.filter(t => wc.thingIds.includes(t.id));
            const thingTreeNodes = this.fillTreeNodes(wcThings);
            this.workCellInnerHTML[wc.id] = this.buildGrid(thingTreeNodes);
        });
        setTimeout(() => this.injectGridElementsComponent(productionLineThings), 100);
        this.productionLineDisplayedValue = this.getProductionLineDisplayedValue(this.productionLine.id);
        this.workCellDisplayedValueMap = this.getWorkCellDisplayedValueMap();
    }

    private fillTreeNodes(things: Thing[]): TreeNode[] {
        return this.treeNodeService.fillTreeNodes(things, "parentThingId");
    }

    private buildGrid(thingTrees: TreeNode[]): any {
        const flatTreeNodes = this.flatTreeService.getFlatTree(thingTrees);
        const maxDepth = this.getMaxDepth(flatTreeNodes);
        let balancedFlatTreeNodes: FlatTreeNode[] = [];
        flatTreeNodes.forEach(node => {                 // add empty elements before leaves to have the leaves at the same level
            if (!node.expandable && node.level < maxDepth) {
                while (node.level < maxDepth) {
                    const emptyNode: FlatTreeNode = { id: null, element: null, expandable: true, level: node.level, name: null };
                    balancedFlatTreeNodes.push(emptyNode);
                    node.level++;
                }
                balancedFlatTreeNodes.push(node);
            } else {
                balancedFlatTreeNodes.push(node);
            }
        });
        return this.createGridTemplate(balancedFlatTreeNodes, maxDepth);
    }

    private createGridTemplate(flatTreeNodes: FlatTreeNode[], maxDepth: number): any {
        let result = '';
        let previousLevel = -1;
        flatTreeNodes.forEach((node, index) => {
            if (node.level > previousLevel) {
                result += '<div class="d-flex grid-cell-container">';
                result += '<div class="d-flex flex-column grid-cell-container">';
                result += this.buildGridCellElementContainer(node, maxDepth);
            } else if (node.level == previousLevel) {
                result += '</div>';
                result += '<div class="d-flex flex-column grid-cell-container">';
                result += this.buildGridCellElementContainer(node, maxDepth);
            } else {
                const levelDifference = previousLevel - node.level;
                result += '</div>';
                for (let i = 0; i < levelDifference; i++) {
                    result += '</div>';
                    result += '</div>';
                }
                result += '<div class="d-flex flex-column grid-cell-container">';
                result += this.buildGridCellElementContainer(node, maxDepth);
            }
            if (index == flatTreeNodes.length - 1) {
                result += '</div>';
                for (let i = 0; i < maxDepth; i++) {
                    result += '</div>';
                    result += '</div>';
                }
                result += '</div>';
            }
            previousLevel = node.level;
        });
        return result;
    }

    private buildGridCellElementContainer(node: FlatTreeNode, maxDepth: number): string {
        const containerClass = node.level == maxDepth ? 'leaf-grid-cell' : 'grid-cell';
        if (node.element) {
            return `<div class="${containerClass} grid-element-id-${node.id}"></div>`
        } else {
            return `<div class="${containerClass}">&nbsp;</div>`
        }
    }

    private injectGridElementsComponent(things: Thing[]): void {
        const productionLineContainer = this.document.getElementById('productionLineContainer_' + this.productionLine.id);
        if (productionLineContainer) {
            things.forEach(thing => {
                const elContainers = Array.from(productionLineContainer.getElementsByClassName('grid-element-id-' + thing.id));
                elContainers.forEach(container => {
                    let elementComponent: ComponentRef<LocationSchemaPlanViewerGridElementComponent> = this.vcRef.createComponent(LocationSchemaPlanViewerGridElementComponent);
                    elementComponent.instance.thingData = this.planViewData.thingDataMap[thing.id];
                    elementComponent.instance.isLinked = this.linkedThingIds?.includes(thing.id);
                    elementComponent.instance.displayedValue = this.getThingDisplayedValue(thing.id);
                    elementComponent.instance.range = this.range;
                    elementComponent.instance.detailsLayout = this.detailsLayout;
                    const componentElement: Element = elementComponent.location.nativeElement;
                    container.appendChild(componentElement);
                });
            });
        }
    }

    private getMaxDepth(flatTreeNodes: FlatTreeNode[]): number {
        return Math.max(...flatTreeNodes.map(node => node.level));
    }

    private getThingDisplayedValue(thingId: string): LocationSchemaPlanViewDisplayedValue {
        return this.planViewData.thingDataMap[thingId].displayedValue;
    }

    private getWorkCellDisplayedValueMap(): { [id: string]: LocationSchemaPlanViewDisplayedValue } {
        let result: { [id: string]: LocationSchemaPlanViewDisplayedValue } = {};
        this.productionLine.workCells?.forEach(wc => {
            result[wc.id] = this.getWorkCellDisplayedValue(wc.id)
        });
        return result;
    }

    private getWorkCellDisplayedValue(workCellId: string): LocationSchemaPlanViewDisplayedValue {
        return this.planViewData.workCellDataMap[workCellId].displayedValue;
    }

    private getProductionLineDisplayedValue(productionLineId: string): LocationSchemaPlanViewDisplayedValue {
        return this.planViewData.productionLineDataMap[productionLineId].displayedValue;
    }

}

export class LocationSchemaPlanViewDisplayedValue {
    value: any;
    class: string;
    filter: any;
    filterArg: any;
    unit: string;
}